import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sparkles,
  Type,
  FileText,
  MessageSquareQuote,
  Zap,
  LayoutTemplate,
  Play,
  Pause,
  Volume2,
  VolumeX,
  ChevronDown,
  Coins,
  Share2,
  Bookmark,
  Wand2,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Real content generated via our own workspace                              */
/*  (Claude Sonnet 4.5 via /api/workspace/generate — pre-baked for speed)     */
/* -------------------------------------------------------------------------- */

const CHAPTERS = [
  {
    id: "prompt",
    number: "01",
    kicker: "Chapter 01 · Type it. Watch it build.",
    title: "The Prompt Console",
    subtitle: "One idea in. A finished draft out.",
    tool: "ideas",
    toolLabel: "Ideas · 10 fresh angles",
    agentLabel: "Claude Sonnet 4.5",
    cost: "1c",
    icon: Sparkles,
    // Free CC0 background music from mixkit — hotlinkable
    audio: "https://assets.mixkit.co/music/download/mixkit-tech-house-vibes-130.mp3",
    // CSS cinematic backdrop
    bgClass: "bg-gradient-to-br from-[#1a0e08] via-[#3d1e0e] to-[#0a0a0a]",
    bgPattern: "radial-gradient(circle at 20% 30%, rgba(217,119,87,0.35), transparent 45%), radial-gradient(circle at 80% 70%, rgba(217,119,87,0.18), transparent 50%)",
    accent: "#d97757",
    prompt: "3 short editorial content ideas for a solo founder launching a mobile coffee cart.",
    output: `1. "The 4 AM Test" — what pre-dawn ops taught me about who really wants this business.

2. "Why I'm Mapping My Competition's Bathroom Breaks" — reframing research as timing gaps.

3. "My Cart Cost Less Than Their Espresso Machine" — how constraints force creative CX.`,
    cta: "Try the Ideas tool",
  },
  {
    id: "tools",
    number: "02",
    kicker: "Chapter 02 · 40 tools. One voice.",
    title: "Pick your Tool",
    subtitle: "Every tool ships a different shape of copy.",
    tool: "thread",
    toolLabel: "Thread · 8-tweet X storm",
    agentLabel: "Claude Sonnet 4.5",
    cost: "1c",
    icon: MessageSquareQuote,
    audio: "https://assets.mixkit.co/music/download/mixkit-dreaming-big-31.mp3",
    bgClass: "bg-gradient-to-br from-[#04140a] via-[#0a2b1a] to-[#000000]",
    bgPattern: "radial-gradient(circle at 30% 20%, rgba(93,168,121,0.4), transparent 45%), radial-gradient(circle at 70% 80%, rgba(93,168,121,0.18), transparent 55%)",
    accent: "#5da879",
    prompt: "A 4-tweet thread on why editorial voice beats AI slop.",
    output: `1/4  Every brand guide says "authentic voice" then approves AI slop that sounds like a LinkedIn bot had a baby with a ToS page. Here's why editorial voice isn't optional.

2/4  AI can mimic structure. It can't mimic judgment. A real editor knows when to break their own rules — when to let a sentence run, when "very" is lazy, and when it's the only word that works.

3/4  Readers don't bounce because your grammar is imperfect. They bounce because 3 paragraphs in, they realize a human didn't write this FOR them.

4/4  Editorial voice = proof someone cared enough to make choices. Every sentence, a micro-decision.`,
    cta: "See all 40 tools",
  },
  {
    id: "agents",
    number: "03",
    kicker: "Chapter 03 · The Fleet.",
    title: "Meet the Agents",
    subtitle: "Sora 2 · GPT-5 · Claude Opus · Gemini Pro · +8 more.",
    tool: "social",
    toolLabel: "Social · 3 captions",
    agentLabel: "Claude Sonnet 4.5",
    cost: "1c",
    icon: Zap,
    audio: "https://assets.mixkit.co/music/download/mixkit-hip-hop-02-738.mp3",
    bgClass: "bg-gradient-to-br from-[#03101f] via-[#0a2540] to-[#000000]",
    bgPattern: "radial-gradient(circle at 25% 25%, rgba(61,139,205,0.4), transparent 45%), radial-gradient(circle at 75% 75%, rgba(61,139,205,0.2), transparent 55%)",
    accent: "#3d8bcd",
    prompt: "3 short LinkedIn captions launching a queer-owned coffee shop in Silverlake.",
    output: `1. We're opening our doors in Silverlake next month. Queer-owned, community-focused, and serving coffee that doesn't compromise. Come find your people here.

2. Built this coffee shop because we wanted a space that felt like ours. Silverlake, get ready for exceptional coffee and a table that's always open for you.

3. New queer-owned coffee shop coming to Silverlake. Great espresso, better vibes, and a community space that celebrates exactly who you are.`,
    cta: "Try a different agent",
  },
  {
    id: "share",
    number: "04",
    kicker: "Chapter 04 · Tweak. Save. Share.",
    title: "Ship it. Earn from it.",
    subtitle: "Refine for 2c. Save as a preset. Share for 30% recurring.",
    tool: "landing",
    toolLabel: "Landing · Page copy pack",
    agentLabel: "Claude Sonnet 4.5",
    cost: "1c",
    icon: LayoutTemplate,
    audio: "https://assets.mixkit.co/music/download/mixkit-serene-view-443.mp3",
    bgClass: "bg-gradient-to-br from-[#160a2a] via-[#3a1f5c] to-[#000000]",
    bgPattern: "radial-gradient(circle at 30% 30%, rgba(192,132,252,0.4), transparent 45%), radial-gradient(circle at 70% 70%, rgba(192,132,252,0.2), transparent 55%)",
    accent: "#c084fc",
    prompt: "Landing copy for a monthly bento box for indie writers.",
    output: `HERO
"Feed Your Creative Soul, Monthly."
Curated tools, prompts and indie press finds — delivered to writers who actually write.

FEATURES
· Fresh inspiration, zero overthinking
· Discover indie voices before Barnes & Noble
· Prompts designed by working authors

Every draft you ship on Draftwave earns you a share link. Share it. Get 30% recurring for every subscriber you send.`,
    cta: "Start creating free",
  },
];

/* -------------------------------------------------------------------------- */
/*  Typewriter (letter-by-letter reveal for the output)                        */
/* -------------------------------------------------------------------------- */
function useTypewriter(text, active, speed = 12) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!active) { setTyped(""); return; }
    let i = 0;
    setTyped("");
    const id = setInterval(() => {
      i += 4; // fast reveal
      if (i >= text.length) { setTyped(text); clearInterval(id); return; }
      setTyped(text.slice(0, i));
    }, speed);
    return () => clearInterval(id);
  }, [text, active, speed]);
  return typed;
}

/* -------------------------------------------------------------------------- */
/*  Chapter — TikTok-style vertical panel                                      */
/* -------------------------------------------------------------------------- */
function Chapter({ c, index, total, active, muted, playing, onTogglePlay, onToggleMute, audioRef }) {
  const Icon = c.icon;
  const typed = useTypewriter(c.output, active);

  return (
    <section
      className={`relative snap-start w-full min-h-[100dvh] flex overflow-hidden text-white ${c.bgClass}`}
      data-testid={`chapter-${c.id}`}
    >
      {/* Cinematic animated backdrop layers */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: c.bgPattern }} />
      <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-overlay grain" />
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          background: "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Pulsing spotlight */}
      <div
        className="absolute inset-0 pointer-events-none animate-pulse"
        style={{ background: `radial-gradient(circle at 50% 50%, ${c.accent}22, transparent 55%)` }}
      />

      {/* Audio track — attached to ref for play/mute control */}
      <audio ref={audioRef} src={c.audio} loop preload="none" data-testid={`chapter-${c.id}-audio`} />

      {/* HUD — top */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 md:p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: c.accent }} />
          <span className="overline text-[10px] tracking-widest opacity-90">{c.kicker}</span>
        </div>
        <div className="hidden md:flex items-center gap-2 border border-white/20 px-3 py-1 text-[10px] font-mono uppercase tracking-widest bg-black/40 backdrop-blur">
          <Icon className="w-3 h-3" style={{ color: c.accent }} />
          {c.toolLabel} · via {c.agentLabel}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 items-end gap-6 p-6 md:p-14 pb-24">
        <div className="lg:col-span-5">
          <div className="text-[110px] md:text-[180px] font-serif-display leading-none opacity-30" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.3)", color: "transparent" }}>
            {c.number}
          </div>
          <h2 className="mt-2 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
            {c.title.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic" style={{ color: c.accent }}>{c.title.split(" ").slice(-1)[0]}</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl opacity-80 max-w-md">{c.subtitle}</p>
          <div className="mt-6 flex items-center gap-4">
            <Link to="/workspace" data-testid={`chapter-${c.id}-cta`}>
              <Button className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs bg-white text-black hover:bg-white/90">
                {c.cta} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <span className="overline text-[10px] tracking-widest opacity-70">120 free credits · no card</span>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-10">
          <div className="border border-white/20 backdrop-blur bg-black/50" data-testid={`chapter-${c.id}-workspace-card`}>
            <div className="flex items-center justify-between border-b border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white/70">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-white/40" />
                  <span className="w-2 h-2 rounded-full bg-white/40" />
                  <span className="w-2 h-2 rounded-full bg-white/40" />
                </div>
                <span>draftwave · workspace</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><Coins className="w-3 h-3" style={{ color: c.accent }} /> {c.cost}</span>
                <span className="hidden md:inline">via {c.agentLabel}</span>
              </div>
            </div>
            <div className="border-b border-white/15 px-4 md:px-5 py-3">
              <div className="overline text-[9px] opacity-60">Prompt</div>
              <div className="mt-1 font-mono text-sm text-white/90">
                <span className="opacity-40">$</span> {c.prompt}
              </div>
            </div>
            <div className="px-4 md:px-5 py-4 min-h-[220px] font-mono text-[13px] leading-relaxed text-white/90">
              <div className="flex items-center gap-2 overline text-[9px] opacity-60 mb-2">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: c.accent }} />
                Live output
              </div>
              <pre className="whitespace-pre-wrap">
                {active ? typed : c.output}
                {active && typed !== c.output && <span className="opacity-70">▍</span>}
              </pre>
            </div>
            <div className="border-t border-white/15 px-4 md:px-5 py-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/70">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><Wand2 className="w-3 h-3" /> Tweak 2c</span>
                <span className="hidden md:flex items-center gap-1"><Bookmark className="w-3 h-3" /> Save preset</span>
                <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> Share</span>
              </div>
              <span className="hidden md:inline opacity-60">{c.toolLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom HUD — playback + progress */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 md:p-6 flex items-center gap-4">
        <button
          onClick={onTogglePlay}
          className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
          aria-label="Play/Pause"
          data-testid={`chapter-${c.id}-play`}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          onClick={onToggleMute}
          className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
          aria-label="Mute"
          data-testid={`chapter-${c.id}-mute`}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <div className="flex items-center gap-2 ml-2">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-1 transition-all ${i === index ? "w-8" : "w-4 opacity-40"}`}
              style={{ background: i === index ? c.accent : "rgba(255,255,255,0.5)" }}
            />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3 overline text-[10px] tracking-widest opacity-70">
          <span className="hidden md:inline">Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page                                                                  */
/* -------------------------------------------------------------------------- */
export default function HowToDraft() {
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const containerRef = useRef(null);
  const audioRefs = useRef([]);

  useEffect(() => {
    if (!containerRef.current) return;
    const els = containerRef.current.querySelectorAll("[data-testid^='chapter-']");
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.55) {
            const idx = Array.from(els).indexOf(e.target);
            if (idx !== -1) setActive(idx);
          }
        }
      },
      { threshold: [0.55, 0.7], root: null }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Only the active chapter's audio plays. Muted controls audibility.
  useEffect(() => {
    audioRefs.current.forEach((a, i) => {
      if (!a) return;
      a.muted = muted;
      if (i === active && playing && !muted) {
        a.currentTime = a.currentTime || 0;
        a.play().catch(() => { /* browser autoplay policy — user gesture needed */ });
      } else {
        a.pause();
      }
    });
  }, [active, playing, muted]);

  function togglePlay() {
    // User gesture — safe to start audio
    setPlaying((p) => !p);
    const a = audioRefs.current[active];
    if (!a) return;
    if (a.paused) { a.muted = muted; a.play().catch(() => {}); } else { a.pause(); }
  }

  function toggleMute() {
    // Unmuting via user gesture — try to start audio if it isn't already
    setMuted((m) => {
      const newMuted = !m;
      const a = audioRefs.current[active];
      if (a) {
        a.muted = newMuted;
        if (!newMuted && playing) a.play().catch(() => {});
      }
      return newMuted;
    });
  }

  function scrollTo(idx) {
    const els = containerRef.current?.querySelectorAll("[data-testid^='chapter-']") || [];
    const target = els[idx];
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="how-to-draft-page">
      <MarketingNav />

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto p-8 md:p-14 grid grid-cols-1 md:grid-cols-12 items-end gap-8">
          <div className="md:col-span-8">
            <div className="overline text-muted-foreground">How To Draft · 4-part demo</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
              Draftwave,
              <br />
              <span className="italic text-primary">in four scrolls.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-muted-foreground">
              Every chapter is real output from our own workspace. Tap the sound icon in any chapter to bring the score in — the demo scores itself.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-2">
            {CHAPTERS.map((c, i) => (
              <button
                key={c.id}
                onClick={() => scrollTo(i)}
                className={`border px-3 py-2 text-left font-mono text-[11px] uppercase tracking-widest transition-colors ${
                  active === i ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"
                }`}
                data-testid={`chapter-jump-${c.id}`}
              >
                <span className="opacity-60">{c.number}</span>
                <span className="ml-2">{c.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <div
        ref={containerRef}
        className="snap-y snap-mandatory h-[100dvh] overflow-y-auto"
        data-testid="chapter-deck"
      >
        {CHAPTERS.map((c, i) => (
          <Chapter
            key={c.id}
            c={c}
            index={i}
            total={CHAPTERS.length}
            active={active === i}
            muted={muted}
            playing={playing && active === i}
            onTogglePlay={togglePlay}
            onToggleMute={toggleMute}
            audioRef={(el) => (audioRefs.current[i] = el)}
          />
        ))}

        <section className="snap-start w-full min-h-[100dvh] flex flex-col items-center justify-center bg-primary text-primary-foreground p-8 relative overflow-hidden" data-testid="chapter-final">
          <div className="absolute inset-0 opacity-20 grain" />
          <div className="relative text-center max-w-3xl">
            <div className="overline opacity-80">You just watched. Now build.</div>
            <h2 className="font-serif-display text-6xl md:text-9xl leading-[0.9] tracking-tight mt-6">
              Your
              <br />
              <span className="italic">turn.</span>
            </h2>
            <p className="mt-8 text-lg opacity-90">
              120 free credits are waiting. No card. No sales call. Ship your first draft in 60 seconds.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link to="/login" data-testid="how-to-draft-cta-signup">
                <Button size="lg" className="rounded-none h-14 px-8 font-mono uppercase tracking-widest text-xs bg-background text-foreground hover:bg-background/90">
                  Get 120 free credits <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/workspace" data-testid="how-to-draft-cta-workspace">
                <Button size="lg" variant="outline" className="rounded-none h-14 px-8 font-mono uppercase tracking-widest text-xs border-background/40 text-background hover:bg-background hover:text-foreground">
                  Open workspace
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
